import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { UserData } from "@/entities/users/types";
import { formatMonthYear } from "@/utils/datetime";
import Text from "@/components/ui/text";
import { ChartDataType } from "@/entities/reporting/types";
import { Organization, People } from "@dreamhub/designsystem/icons";
import UserAvatar from "@/components/controls/UserAvatar";

interface TooltipRowProps {
  icon: ReactNode;
  value: number | string;
  label: string;
  isPercentage?: boolean;
}

export const CustomTooltipArrow = () => {
  return (
    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-t-[10px] border-t-surface-tooltip-toast"></div>
  );
};
export const TooltipRow: React.FC<TooltipRowProps> = ({
  icon,
  value,
  label,
  isPercentage = false
}) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-[24px_auto_1fr] items-center gap-2">
      <div className="flex justify-center items-center text-primary">
        {icon}
      </div>
      <Text>
        {value}
        {isPercentage ? "%" : ` ${t(`leadAnalysis.tooltips.leads`)}`}
      </Text>
      <Text className="text-tertiary ml-2"> {label}</Text>
    </div>
  );
};

export const LeadAnalysisCustomTooltip: React.FC<{
  data: ChartDataType;
  userData?: UserData;
  showComparison?: boolean;
}> = ({ data, userData, showComparison }) => {
  const { t } = useTranslation();

  const date = formatMonthYear(data.date);
  const youValue = Math.round(data.youValue);
  const teamValue = Math.round(data.teamValue);
  const organizationValue = Math.round(data.organizationValue);

  return (
    <div>
      <Text
        as="div"
        data-test="lead-analysis-tooltip"
        variant="body"
        className="z-50 max-w-250 overflow-hidden rounded-md border-0 bg-surface-tooltip-toast px-3 py-2 shadow-md "
      >
        <div className="max-h-32 w-full flex flex-col">
          <div className="mb-2 text-secondary text-sm">{date}</div>
          {showComparison ? (
            <div className="flex flex-col gap-2">
              <TooltipRow
                icon={
                  <UserAvatar
                    size="M"
                    userData={userData}
                  />
                }
                value={youValue}
                label={t(`leadAnalysis.tooltips.you`)}
              />

              <TooltipRow
                icon={<People />}
                value={teamValue}
                label={t(`leadAnalysis.tooltips.team`)}
              />

              <TooltipRow
                icon={<Organization />}
                value={organizationValue}
                label={t(`leadAnalysis.tooltips.organization`)}
              />
            </div>
          ) : (
            <Text>
              {youValue} {t(`leadAnalysis.tooltips.leads`)}
            </Text>
          )}
        </div>
      </Text>
      <CustomTooltipArrow />
    </div>
  );
};

export const DirectReportCustomTooltip: React.FC<{
  data: any;
  userMap: Map<string, UserData>;
  isPercentage?: boolean;
}> = ({ data, userMap, isPercentage = false }) => {
  const { month, originalMonth, ...usersTooltipData } = data;

  const date = formatMonthYear(month);
  return (
    <div>
      <Text
        as="div"
        data-test="report-tooltip"
        variant="body"
        className="z-50 max-w-250 overflow-hidden rounded-md border-0 bg-surface-tooltip-toast px-3 py-2 shadow-md "
      >
        <div className="w-full flex flex-col">
          <div className="mb-2 text-secondary text-sm">{date}</div>

          <div className="flex flex-col gap-2">
            {Object.entries(usersTooltipData).map(([userId, value]) => (
              <TooltipRow
                key={userId}
                icon={
                  <UserAvatar
                    size="M"
                    userData={userMap.get(userId)}
                  />
                }
                value={value as number}
                label={userMap.get(userId)?.displayName || userId}
                isPercentage={isPercentage}
              />
            ))}
          </div>
        </div>
      </Text>
      <CustomTooltipArrow />
    </div>
  );
};
